import * as React from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { useIntl } from "react-intl";
import { toast } from "sonner";

import { ConfirmationModal } from "@/components/Modals/ConfirmationModal";
import { PageActionProps } from "@/components/PageTitle";
import { Alert, Button, confirmDialog, Dialog } from "@/components/ui";
import { Archive, ArrowRight, CircleAlert, CircleCheck, CircleDashed, FolderOpen, Loader2 } from "lucide-react";
import { CoachSettings, SocialNetworkPostStatus, SocialNetworksId, UploadProgressMap } from "@/interfaces";

import {
  URLS,
  editorQueueListUrl,
  capitalizeFormatter,
  formatHexColor,
  usePlayerSettings,
  useComplianceChecker,
  useSocialNetworks,
  useSocialCoachSession,
  getErrorMessage,
} from "@/utils";
import {
  ContentPageServices,
  EDITOR_VIDEO_PROJECT_EXTRA_FIELDS,
  Media,
  User,
  UsersServices,
  VideoProject,
  VideoProjectServices,
} from "@/services/bedrock";

import { EditorProjectFiles } from "./components/EditorProjectFiles";
import { EditorPostInfo } from "./components/EditorPostInfo";
import { CaptionCoachDialog } from "@/components/CaptionCoach/CaptionCoachDialog";
import { DuplicateVideoProjectModal } from "./components/DuplicateVideoProjectModal";
import { VideoProjectPageType, descriptors } from "./descriptors";
import {
  hasEditedVideo,
  hasRequiredEditedSrt,
  prepareEditedSubtitle,
  toEditedMediaPayload,
} from "./editedDeliverables";
import { validateMediasRequiremenst } from "./validations";

import { VideoProjectBubbleDetails } from "./components/BubbleDetails";
import { ProjectFileUploader } from "@/utils/projectFileUploader";
import { UploadProgressModal } from "@/components/UploadProgressModal";
import { buildPreferenceOptions, StylePreferencesOptions } from "@/utils/projectHelpers";
import { ClientNotesComponent } from "./components/ClientNotesComponent";
import { StylePreferencesComponent } from "./components/StylePreferencesComponent";
import { EditorPlayerInfo } from "./components/EditorPlayerInfo";
import { VideoProjectStatusColumnId } from "@/components/VideoProjectStatusPicker";
import { PageWrapper } from "@/components/PageWrapper";
import {
  assignedUserIdForRole,
  assigningRoleForEvent,
  confirmEventSamePersonAssignment,
  resolveStaffActorUserId,
} from "@/components/VideoProjectStaffPicker";
import { ProjectInfoCard } from "@/containers/Editor/VideoProjectPage/components/ProjectInfoCard";
import { VideoProjectHistoryPanel } from "@/containers/Editor/VideoProjectPage/components/VideoProjectHistoryPanel";
import { EditorFinalEdit } from "@/containers/Editor/VideoProjectPage/components/EditorFinalEdit";
import { QaReturnedCallout } from "@/containers/Editor/VideoProjectPage/components/QaReturnedCallout";
import { VideoProjectQaStudio } from "@/containers/Editor/VideoProjectPage/components/VideoProjectQaStudio";
import { isQaReviewStatus, isQaStudioReadOnly, shouldWarnSelfQa } from "@/containers/Editor/VideoProjectPage/qaReview";
import { useInvalidateVideoProjectEventLogs } from "@/containers/Editor/VideoProjectPage/components/VideoProjectHistoryPanel";
import { EditorVideoProjectField } from "./components/EditorCaptionSettings";
import { captureEditorDraft, EditorDraftSnapshot, hasEditorDraftChanges } from "./editorDraft";
import { EditorPageActionSpec, getEditorPageActions } from "./pageActions";
import { resolveEditorStatusColumnAction } from "./statusColumnAction";
import { useQuery, useQueryClient } from "@tanstack/react-query";

function applyEditorField(project: VideoProject, field: EditorVideoProjectField, value: any): VideoProject {
  if (field === "message") {
    const post = project.post ?? {
      id: project.postId ?? -1,
      playerId: project.playerId,
      message: "",
      postDate: "",
      media: [],
      postSocialNetworks: [],
    };
    return { ...project, post: { ...post, message: value } };
  }

  if (field === "socialNetworkIds") {
    if (!project.post) return project;
    const ids = (value ?? []) as SocialNetworksId[];
    const existing = project.post.postSocialNetworks ?? [];
    const postSocialNetworks = ids.map(
      id =>
        existing.find(sn => sn.socialNetworkId === id) ?? {
          id: -1,
          socialNetworkId: id,
          status: SocialNetworkPostStatus.PENDING,
          externalPostUrl: null,
        }
    );
    return { ...project, post: { ...project.post, postSocialNetworks } };
  }

  return { ...project, [field]: value };
}

export function EditorVideoProjectPage() {
  const history = useHistory();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  const { formatMessage } = useIntl();
  const { userId, canManageVideoProjectQueue } = useSocialCoachSession();
  const currentUserId = resolveStaffActorUserId(userId);
  const [openArchiveModal, setOpenArchiveModal] = React.useState<boolean>(false);
  const [openDuplicateModal, setOpenDuplicateModal] = React.useState<boolean>(false);
  const [submitEventDialogOpen, setSubmitEventDialogOpen] = React.useState<boolean>(false);
  const [qaModalOpen, setQaModalOpen] = React.useState(false);
  const [startingQaReview, setStartingQaReview] = React.useState(false);
  const [reopeningProject, setReopeningProject] = React.useState(false);
  const [savingStatus, setSavingStatus] = React.useState(false);
  const queryClient = useQueryClient();
  const invalidateEventLogs = useInvalidateVideoProjectEventLogs();
  const autoOpenedQaRef = React.useRef(false);
  const { data: editorsData } = useQuery({
    queryKey: ["editors"],
    queryFn: () => UsersServices.getUsers({ userType: User.Type.EDITOR }),
    staleTime: 1000 * 60 * 30,
  });
  const staffUsers = editorsData?.content || [];
  const [loading, setLoading] = React.useState<boolean>(false);
  const [uploadingFiles, setUploadingFiles] = React.useState(false);
  const [submittingProject, setSubmittingProject] = React.useState<boolean>(false);
  const [savingProject, setSavingProject] = React.useState<boolean>(false);
  const [savedDraft, setSavedDraft] = React.useState<EditorDraftSnapshot | null>(null);
  const [uploadProgressMap, setUploadProgressMap] = React.useState<UploadProgressMap>();
  const [formErrorMessages, setFormErrorMessages] = React.useState<string[] | undefined>(undefined);

  const [videoProject, setVideoProject] = React.useState<VideoProject | undefined>(undefined);
  const [videoProjectMessages, setVideoProjectMessages] = React.useState<VideoProject.Message[] | undefined>(undefined);
  const [needsAck, setNeedsAck] = React.useState<boolean>(false);
  const [playerNotes, setPlayerNotes] = React.useState<VideoProject.ClientNote | undefined>(undefined);
  const [coachNotes, setCoachNotes] = React.useState<VideoProject.ClientNote | undefined>(undefined);

  const [preferencesInfo, setPreferencesInfo] = React.useState<StylePreferencesOptions[]>([]);

  const isEditing = videoProject?.status === VideoProject.Status.EDITING;
  const isCompleted = VideoProject.isCompletedStatus(videoProject?.status);
  const editable = !!videoProject && !videoProject.archived && isEditing && !!videoProject.post;

  const [industryRole, setIndustryRole] = React.useState<string | null>(null);

  const [hashtags, setHashtags] = React.useState<string>("");
  const [initialHashtags, setInitialHashtags] = React.useState<string>("");
  const [showAIModal, setShowAIModal] = React.useState<boolean>(false);
  const [transcript, setTranscript] = React.useState<string | undefined>(undefined);
  const [blockedWordsInCaption, setBlockedWordsInCaption] = React.useState<string>();

  const { hasSetting } = usePlayerSettings(videoProject?.playerId);
  const showHashtagToggle = hasSetting(CoachSettings.PLAYER_HASHTAG, true);

  const { findBlockedWords } = useComplianceChecker({
    playerId: videoProject?.playerId,
    coachId: videoProject?.coachId,
  });

  const { postableSocialNetworks } = useSocialNetworks(true, videoProject?.coachId);

  const validateMessage = (message?: string) => {
    const blockedWords = findBlockedWords(message || "");
    if (blockedWords.length > 0) {
      setBlockedWordsInCaption(blockedWords.join(", "));
    } else {
      setBlockedWordsInCaption(undefined);
    }
  };

  React.useEffect(() => {
    validateMessage(videoProject?.post?.message);
  }, [videoProject?.post?.message, findBlockedWords]);

  React.useEffect(() => {
    const transcripts = videoProject?.post?.media
      ?.flatMap(m => m.transcripts?.map(t => t.content))
      ?.sort(t => t?.length || 0)
      ?.reverse();
    if (transcripts && transcripts.length > 0) {
      setTranscript(transcripts[0]);
    } else {
      setTranscript(undefined);
    }
  }, [videoProject?.post?.media]);

  const formDisabled = !editable;
  const hasUnsavedChanges = React.useMemo(() => {
    if (!videoProject) return false;
    return hasEditorDraftChanges(savedDraft, captureEditorDraft(videoProject, hashtags));
  }, [videoProject, hashtags, savedDraft]);

  const rememberSavedDraft = (project: VideoProject, tags: string) => {
    setSavedDraft(captureEditorDraft(project, tags));
  };

  const applyLoadedProject = (nextProject: VideoProject) => {
    const tags = nextProject.post?.hashtags || "";
    setVideoProject(nextProject);
    setPlayerNotes(nextProject.clientNotes);
    setCoachNotes(nextProject.coachClientNotes);
    setVideoProjectMessages(nextProject.messages ?? []);
    setNeedsAck(!!nextProject.needsAck);
    setHashtags(tags);
    setInitialHashtags(tags);
    rememberSavedDraft(nextProject, tags);
  };

  const socialNetworksPosted = videoProject?.post?.postSocialNetworks || [];
  const [selectedSocialNetwork, setSelectedSocialNetwork] = React.useState<SocialNetworksId | undefined>(undefined);

  React.useEffect(() => {
    if (socialNetworksPosted.length > 0 && !selectedSocialNetwork) {
      setSelectedSocialNetwork(socialNetworksPosted[0]?.socialNetworkId);
    }
  }, [socialNetworksPosted]);

  const withCaption = React.useMemo(() => {
    return socialNetworksPosted.some(sn => sn.caption && sn.caption.length > 0);
  }, [socialNetworksPosted]);

  const finalMessage = React.useMemo(() => {
    if (withCaption) {
      const socialPost = socialNetworksPosted.find(sn => sn.socialNetworkId === selectedSocialNetwork);
      return socialPost?.caption || "";
    } else {
      return videoProject?.post?.message || "";
    }
  }, [selectedSocialNetwork, socialNetworksPosted, videoProject?.post?.message, withCaption]);

  const hasCaptionCoach = React.useMemo(() => {
    return hasSetting(CoachSettings.CAPTION_COACH, true);
  }, [hasSetting]);

  const handleUpdateField = (field: EditorVideoProjectField, value: any) => {
    setVideoProject(prev => {
      if (!prev) return prev;
      return applyEditorField(prev, field, value);
    });
  };

  const handleMediaUpdated = (newMedia: Media[]) => {
    setVideoProject(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        editedMedia: newMedia.map((item, sortOrder) => ({
          ...item,
          sortOrder,
        })),
      };
    });
  };

  React.useEffect(() => {
    if (params["id"]) {
      // Fast Refresh re-runs this effect. Skip the loader/refetch when this
      // project is already on screen so copy edits don't blank the page.
      if (videoProject?.id === Number(params["id"])) return;

      setSelectedSocialNetwork(undefined);
      setLoading(true);
      VideoProjectServices.show(params["id"], EDITOR_VIDEO_PROJECT_EXTRA_FIELDS)
        .then(nextProject => {
          applyLoadedProject(nextProject);
          setIndustryRole(nextProject.player?.industryRoles?.[0]?.name || null);
        })
        .catch(() => {
          alert("Error getting video project info");
          history.goBack();
        })
        .finally(() => {
          setLoading(false);
        });
      VideoProjectServices.acknowledge(Number(params["id"])).catch(e => {
        console.error("acknowledge error", e);
      });
    }
  }, [params["id"]]);

  const getErrors = (): string[] | undefined => {
    const errors: string[] = [];
    const validMedias = validateMediasRequiremenst(videoProject?.editedMedia);

    if (!validMedias) {
      errors.push(
        formatMessage({
          ...descriptors[VideoProjectPageType.wrongSpecificationsMessage],
        })
      );
    }

    if (videoProject?.editedMedia !== undefined && videoProject.editedMedia.length > 0) {
      videoProject.editedMedia.forEach(media => {
        if (!media.validAspectRatio && media.validAspectRatio !== undefined) {
          errors.push(
            formatMessage(
              {
                ...descriptors[VideoProjectPageType.wrongAspectRatioMessage],
              },
              { type: media.type.split("/")[0], typeAfterDot: capitalizeFormatter(media.type.split("/")[0]) }
            )
          );
        }

        if (media.errors?.wrongBitrate) {
          errors.push(
            formatMessage({
              ...descriptors[VideoProjectPageType.wrongBitrateMessage],
            })
          );
        }
        if (media.errors?.wrongLength) {
          errors.push(
            formatMessage({
              ...descriptors[VideoProjectPageType.wrongDurationoMessage],
            })
          );
        }
        if (media.errors?.wrongSize) {
          errors.push(
            formatMessage(
              {
                ...descriptors[VideoProjectPageType.wrongSizeMessage],
              },
              { type: media.type.split("/")[0], typeAfterDot: capitalizeFormatter(media.type.split("/")[0]) }
            )
          );
        }
      });
    }

    return errors.length > 0 ? errors : undefined;
  };

  React.useEffect(() => {
    setFormErrorMessages(() => getErrors());
  }, [videoProject]);

  const validateSubmit = (): boolean => {
    if (!videoProject) return false;

    const errors: string[] = [];
    if (!videoProject.post?.message || !videoProject.post.message.trim()) {
      errors.push("Caption is required");
    }

    if (!videoProject.editedMedia?.some(hasEditedVideo)) {
      errors.push("Final Edit is required");
    }

    if (errors.length > 0) {
      setFormErrorMessages(errors);
      return false;
    }

    setFormErrorMessages(undefined);
    return true;
  };

  const submitProject = async (
    event: VideoProject.Event.SUBMIT_FOR_QA_REVIEW | VideoProject.Event.COMPLETE,
    options?: { skipOptionalQaPrompt?: boolean }
  ): Promise<void> => {
    if (!videoProject || !validateSubmit()) return;

    if (event === VideoProject.Event.COMPLETE && !videoProject.requiresQa && !options?.skipOptionalQaPrompt) {
      setSubmitEventDialogOpen(true);
      return;
    }

    setSubmitEventDialogOpen(false);
    const submitEvent = event;

    const { editedMedia, playerId, preferredPostDate } = videoProject;
    const mediaToUpload = editedMedia?.filter(hasEditedVideo) ?? [];
    if (mediaToUpload.length === 0) return;

    const uploader = new ProjectFileUploader("videoProject", playerId);
    const uploadMap: UploadProgressMap = {};
    mediaToUpload.forEach((mediaObj: Media) => {
      uploadMap[mediaObj.id] = { progress: 0, task: undefined };
    });
    setUploadProgressMap(uploadMap);
    setUploadingFiles(true);
    const preparedMedia = await uploader.prepareMedia(mediaToUpload, (fileId, progress) => {
      setUploadProgressMap(map => {
        return { ...map, [fileId]: { progress, task: undefined } };
      });
    });
    const editedSubtitle = await prepareEditedSubtitle(uploader, preparedMedia);
    setUploadingFiles(false);
    setSubmittingProject(true);

    const videoProjectId = videoProject.id;
    const contentPagesPromise = ContentPageServices.getContentPages({ videoProjectId, perPage: 100 });
    const submitViaEvents = async () => {
      const latest = await VideoProjectServices.show(videoProjectId);
      const isResubmit =
        latest.status === VideoProject.Status.READY_TO_POST ||
        latest.status === VideoProject.Status.SCHEDULED ||
        latest.status === VideoProject.Status.POST_FAILED ||
        latest.status === VideoProject.Status.SUBMITTED_FOR_QA_REVIEW ||
        latest.status === VideoProject.Status.QA_REVIEW;

      if (latest.status === VideoProject.Status.SUBMITTED_FOR_EDITING) {
        if (!currentUserId) {
          throw new Error("Unable to start editing without a current user");
        }
        await VideoProjectServices.update(videoProjectId, {
          event: VideoProject.Event.START_EDITING,
          editorUserId: currentUserId,
        });
      }

      await VideoProjectServices.update(videoProjectId, {
        event: isResubmit ? VideoProject.Event.RESUBMIT : submitEvent,
        editedMedia: toEditedMediaPayload(preparedMedia),
        ...(editedSubtitle ? { editedSubtitle } : {}),
        message: videoProject.post?.message,
        hashtags: hashtags || undefined,
        isAutoPost: videoProject.isAutoPost,
        preferredPostDate: preferredPostDate ?? undefined,
        allowShortLink: videoProject.allowShortLink,
        allowCompliance: videoProject.allowCompliance,
        allowPlayerHashtags: videoProject.allowPlayerHashtags,
        socialNetworkIds: videoProject.post?.postSocialNetworks?.map(sn => sn.socialNetworkId),
      });
    };

    Promise.all([contentPagesPromise, submitViaEvents()])
      .then(async ([contentPages]) => {
        if (contentPages.content.length > 0) {
          const originalMedia = preparedMedia.map(media => ({ ...media, id: -1 }));
          const updateContentPagePromises = contentPages.content.map(contentPage => {
            return ContentPageServices.updateContentPage(contentPage.id, { originalMedia });
          });
          await Promise.all(updateContentPagePromises);
        }
        toast.success("Project submitted successfully!");
        const nextProject = await VideoProjectServices.show(videoProjectId, EDITOR_VIDEO_PROJECT_EXTRA_FIELDS);
        applyLoadedProject(nextProject);
        invalidateEventLogs(nextProject.id);
        queryClient.invalidateQueries({ queryKey: ["videoProjectAssignments", nextProject.id] });
      })
      .catch(e => {
        setFormErrorMessages([String(e).substring(0, 100)]);
      })
      .finally(() => {
        setSubmittingProject(false);
      });
  };

  const saveDraft = async (): Promise<void> => {
    if (!videoProject || !hasUnsavedChanges) return;

    const { editedMedia, playerId, preferredPostDate } = videoProject;
    const mediaToUpload = (editedMedia ?? []).filter(hasEditedVideo);

    setSavingProject(true);
    try {
      let preparedMedia = mediaToUpload;
      const uploader = mediaToUpload.length > 0 ? new ProjectFileUploader("videoProject", playerId) : undefined;
      if (uploader) {
        const uploadMap: UploadProgressMap = {};
        mediaToUpload.forEach((mediaObj: Media) => {
          uploadMap[mediaObj.id] = { progress: 0, task: undefined };
        });
        setUploadProgressMap(uploadMap);
        setUploadingFiles(true);
        preparedMedia = await uploader.prepareMedia(mediaToUpload, (fileId, progress) => {
          setUploadProgressMap(map => {
            return { ...map, [fileId]: { progress, task: undefined } };
          });
        });
        setUploadingFiles(false);
      }

      const editedSubtitle = uploader ? await prepareEditedSubtitle(uploader, preparedMedia) : undefined;

      await VideoProjectServices.update(videoProject.id, {
        event: VideoProject.Event.SAVE_DRAFT,
        ...(preparedMedia.length > 0
          ? {
              editedMedia: toEditedMediaPayload(preparedMedia),
            }
          : {}),
        ...(editedSubtitle ? { editedSubtitle } : {}),
        message: videoProject.post?.message,
        hashtags,
        isAutoPost: videoProject.isAutoPost,
        preferredPostDate: preferredPostDate ?? undefined,
        allowShortLink: videoProject.allowShortLink,
        allowCompliance: videoProject.allowCompliance,
        allowPlayerHashtags: videoProject.allowPlayerHashtags,
        socialNetworkIds: videoProject.post?.postSocialNetworks?.map(sn => sn.socialNetworkId),
      });

      const nextProject = await VideoProjectServices.show(videoProject.id, EDITOR_VIDEO_PROJECT_EXTRA_FIELDS);
      applyLoadedProject(nextProject);
      invalidateEventLogs(nextProject.id);
      queryClient.invalidateQueries({ queryKey: ["videoProjectAssignments", nextProject.id] });
      toast.success(formatMessage({ ...descriptors[VideoProjectPageType.savedMessage] }));
    } catch (error) {
      setFormErrorMessages([getErrorMessage(error) || String(error).substring(0, 100)]);
    } finally {
      setUploadingFiles(false);
      setSavingProject(false);
    }
  };

  const applyQueueUpdate = (updated: VideoProject) => {
    setVideoProject(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        status: updated.status ?? prev.status,
        editorUserId: updated.editorUserId ?? prev.editorUserId,
        qaUserId: updated.qaUserId ?? prev.qaUserId,
      };
    });
    if (updated.id) {
      invalidateEventLogs(updated.id);
      queryClient.invalidateQueries({ queryKey: ["videoProjectAssignments", updated.id] });
    }
  };

  const reopenProject = async () => {
    if (!videoProject || !isCompleted || videoProject.archived) return;

    setReopeningProject(true);
    try {
      const updated = await VideoProjectServices.update(videoProject.id, {
        event: VideoProject.Event.REOPEN_EDITING,
      });
      applyQueueUpdate(updated);
      toast.success("Project reopened for editing");
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to reopen project");
    } finally {
      setReopeningProject(false);
    }
  };

  const runStatusEvent = async (request: { event: VideoProject.Event; userId?: number }) => {
    if (!videoProject || videoProject.archived) return;

    const role = assigningRoleForEvent(request.event);
    const actorUserId = role
      ? assignedUserIdForRole({ editorUserId: videoProject.editorUserId, qaUserId: videoProject.qaUserId }, role) ??
        request.userId ??
        currentUserId
      : request.userId;
    if (role && actorUserId == null) {
      toast.error("Cannot update project status");
      return;
    }

    const confirmed = await confirmEventSamePersonAssignment(
      { editorUserId: videoProject.editorUserId, qaUserId: videoProject.qaUserId },
      request.event,
      actorUserId,
      staffUsers
    );
    if (!confirmed) return;

    if (request.event === VideoProject.Event.REJECT_QA || request.event === VideoProject.Event.APPROVE_QA) {
      void beginQaReview();
      return;
    }

    setSavingStatus(true);
    try {
      const updated = await VideoProjectServices.update(videoProject.id, {
        event: request.event,
        ...(request.event === VideoProject.Event.START_EDITING && actorUserId != null
          ? { editorUserId: actorUserId }
          : {}),
      });
      applyQueueUpdate(updated);
      toast.success("Status updated successfully!");
    } catch (error) {
      toast.error(getErrorMessage(error) || "Failed to update status");
    } finally {
      setSavingStatus(false);
    }
  };

  const beginQaReview = async () => {
    if (!videoProject || !canManageVideoProjectQueue || videoProject.archived) return;

    const isStartingQaReview = videoProject.status === VideoProject.Status.SUBMITTED_FOR_QA_REVIEW;
    if (isStartingQaReview && shouldWarnSelfQa(currentUserId, videoProject.editorUserId)) {
      const confirmed = await confirmDialog({
        title: "Review your own edit?",
        description:
          "You are the assigned editor on this project. Reviewing your own cut can miss issues that another reviewer would catch.",
        confirmLabel: "Review anyway",
        cancelLabel: "Cancel",
      });
      if (!confirmed) return;
    }

    if (isStartingQaReview) {
      setStartingQaReview(true);
      try {
        const updated = await VideoProjectServices.update(videoProject.id, {
          event: VideoProject.Event.START_QA_REVIEW,
        });
        applyQueueUpdate(updated);
      } catch (error) {
        setFormErrorMessages([String(error).substring(0, 100)]);
        return;
      } finally {
        setStartingQaReview(false);
      }
    }

    setQaModalOpen(true);
  };

  const openQaStudio = () => setQaModalOpen(true);

  const handleStatusColumnSelect = (columnId: VideoProjectStatusColumnId) => {
    if (!videoProject) return;

    const action = resolveEditorStatusColumnAction(videoProject.status, columnId, {
      requiresQa: videoProject.requiresQa,
      currentUserId,
    });
    if (!action) return;

    if (action.type === "submit") {
      void submitProject(action.event);
      return;
    }

    if (action.type === "qa_review") {
      void beginQaReview();
      return;
    }

    void runStatusEvent(action);
  };

  React.useEffect(() => {
    const shouldOpenQa = new URLSearchParams(location.search).get("qa") === "1";
    if (!shouldOpenQa || autoOpenedQaRef.current || !videoProject) return;
    autoOpenedQaRef.current = true;
    if (canManageVideoProjectQueue && isQaReviewStatus(videoProject.status) && !videoProject.archived) {
      void beginQaReview();
      return;
    }
    openQaStudio();
  }, [canManageVideoProjectQueue, location.search, videoProject]);

  const resolvePageAction = (spec: EditorPageActionSpec): PageActionProps => {
    const isLoading =
      uploadingFiles ||
      loading ||
      submittingProject ||
      savingProject ||
      startingQaReview ||
      reopeningProject ||
      savingStatus;
    const { intent } = spec;

    switch (intent.type) {
      case "qa_review":
        return { text: spec.text, onClick: () => void beginQaReview(), loading: startingQaReview };
      case "view_qa":
        return { text: spec.text, onClick: openQaStudio };
      case "reopen":
        return { text: spec.text, onClick: () => void reopenProject(), loading: reopeningProject };
      case "save":
        return {
          text: spec.text,
          onClick: () => void saveDraft(),
          disabled: !hasUnsavedChanges || isLoading,
          loading: savingProject,
        };
      case "submit":
        return { text: spec.text, onClick: () => void submitProject(intent.event), loading: isLoading };
      case "status_event":
        return {
          text: spec.text,
          onClick: () => void runStatusEvent({ event: intent.event, userId: intent.userId }),
          disabled: isLoading,
        };
      case "duplicate":
        return {
          text: spec.text,
          icon: spec.icon,
          onClick: () => setOpenDuplicateModal(true),
          disabled: isLoading,
        };
      case "archive":
        return {
          text: spec.text,
          icon: spec.icon,
          onClick: () => setOpenArchiveModal(true),
          disabled: isLoading,
        };
    }
  };

  const getPageActions = (): {
    primary?: PageActionProps;
    secondary?: PageActionProps;
    menu: PageActionProps[];
  } => {
    if (!videoProject) return { menu: [] };

    const mapping = getEditorPageActions({
      status: videoProject.status,
      archived: videoProject.archived,
      requiresQa: videoProject.requiresQa,
      qaStatus: videoProject.qaStatus,
      revisionCount: videoProject.revisionCount,
      canManageVideoProjectQueue,
      hasPost: !!videoProject.post,
      currentUserId,
    });

    return {
      primary: mapping.primary ? resolvePageAction(mapping.primary) : undefined,
      secondary: mapping.secondary ? resolvePageAction(mapping.secondary) : undefined,
      menu: mapping.menu.map(resolvePageAction),
    };
  };

  const archiveModal = (): JSX.Element => {
    let title = formatMessage({ ...descriptors[VideoProjectPageType.archiveAlertTitle] });
    let okHandler = () => {
      VideoProjectServices.update(videoProject!.id, {
        event: VideoProject.Event.ARCHIVE,
      })
        .then(() => {
          history.push(editorQueueListUrl());
        })
        .catch((e: any) => {
          alert(e);
        });
    };

    return (
      <ConfirmationModal
        openConfirmationModal={openArchiveModal}
        title={title}
        onClose={() => setOpenArchiveModal(false)}
        rejectHandler={() => setOpenArchiveModal(false)}
        okHandler={okHandler}
      />
    );
  };

  const duplicateModal = (): JSX.Element | undefined => {
    if (!videoProject) return undefined;

    const onDuplicateHandler = (duplicatedProject: VideoProject) => {
      setOpenDuplicateModal(false);
      history.push(URLS.editor.videoProjects.detail.replace(":id", `${duplicatedProject.id}`));
    };

    return (
      <DuplicateVideoProjectModal
        open={openDuplicateModal}
        videoProjectId={videoProject.id}
        isVideoProjectArchived={!!videoProject.archived}
        onClose={() => setOpenDuplicateModal(false)}
        onDuplicate={onDuplicateHandler}
      />
    );
  };

  const refreshMessages = (): void => {
    VideoProjectServices.show(videoProject?.id!!, { messages: true }).then(r => {
      setVideoProjectMessages(r.messages ?? []);
    });
  };

  React.useEffect(() => {
    setPreferencesInfo(buildPreferenceOptions(videoProject?.preferences));
  }, [videoProject?.preferences]);

  const pageActions = getPageActions();

  return (
    <PageWrapper
      title={`Video Project #${videoProject?.id ?? ""}`}
      primaryAction={pageActions.primary ?? null}
      secondaryAction={pageActions.secondary}
      actions={pageActions.menu}
      loading={loading}
      breadcrumbs={[{ name: "Editing Queue", link: editorQueueListUrl() }]}
    >
      {archiveModal()}
      {duplicateModal()}

      <Dialog open={submitEventDialogOpen} onOpenChange={setSubmitEventDialogOpen} size="md" showCloseButton>
        <Dialog.Header className="border-b-0 pb-1">
          <Dialog.Title>Submit edit</Dialog.Title>
          <Dialog.Description>How should this project move forward?</Dialog.Description>
        </Dialog.Header>
        <Dialog.Content className="flex flex-col gap-2.5 pt-1">
          <button
            type="button"
            onClick={() => void submitProject(VideoProject.Event.SUBMIT_FOR_QA_REVIEW)}
            className="group flex w-full cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left transition-all hover:border-indigo-300 hover:bg-indigo-50/60 hover:shadow-sm"
          >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-500 transition-colors group-hover:bg-indigo-100">
              <CircleDashed className="size-5" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-sm font-semibold text-slate-800">Submit for QA Review</span>
              <span className="text-xs leading-relaxed text-slate-500">
                A reviewer will check this cut before it goes live.
              </span>
            </span>
            <ArrowRight className="mt-2.5 size-4 shrink-0 text-slate-300 transition-all group-hover:translate-x-0.5 group-hover:text-indigo-500" />
          </button>
          <button
            type="button"
            onClick={() => void submitProject(VideoProject.Event.COMPLETE, { skipOptionalQaPrompt: true })}
            className="group flex w-full cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 text-left transition-all hover:border-emerald-300 hover:bg-emerald-50/60 hover:shadow-sm"
          >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-500 transition-colors group-hover:bg-emerald-100">
              <CircleCheck className="size-5" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-sm font-semibold text-slate-800">Complete</span>
              <span className="text-xs leading-relaxed text-slate-500">Mark it done and ready to post.</span>
            </span>
            <ArrowRight className="mt-2.5 size-4 shrink-0 text-slate-300 transition-all group-hover:translate-x-0.5 group-hover:text-emerald-500" />
          </button>
        </Dialog.Content>
      </Dialog>

      <Dialog open={submittingProject} onOpenChange={undefined} size="sm" disableEscapeDismissal={true}>
        <Dialog.Header>
          <Dialog.Title className="flex items-center gap-1.5 text-slate-800 text-sm font-semibold select-none">
            <Loader2 className="animate-spin size-4 text-indigo-600 shrink-0" />
            <span>Submitting Project</span>
          </Dialog.Title>
        </Dialog.Header>

        <Dialog.Content className="p-5 flex flex-col items-center justify-center gap-4 text-center">
          <div className="text-xs text-slate-600 font-medium">
            Please wait while we submit your video project changes. This may take a few moments.
          </div>
        </Dialog.Content>
      </Dialog>

      {videoProject && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3 flex flex-col gap-6">
              {videoProject.archived && (
                <Alert variant="warning">
                  <Archive />
                  <Alert.Title>This project is archived</Alert.Title>
                  <Alert.Description>
                    It cannot be edited. Duplicate it if you need a new project from this one.
                  </Alert.Description>
                </Alert>
              )}
              {isCompleted && !videoProject.archived && (
                <Alert>
                  <FolderOpen />
                  <Alert.Title>This project is completed</Alert.Title>
                  <Alert.Description className="flex flex-col items-start gap-2">
                    <p>
                      Media, caption, hashtags, and publishing settings are read-only. Re-open the project to edit them.
                    </p>
                    <Button size="sm" variant="outline" onClick={() => void reopenProject()} loading={reopeningProject}>
                      Re-open project
                    </Button>
                  </Alert.Description>
                </Alert>
              )}
              {videoProject.status === VideoProject.Status.POST_FAILED && !videoProject.archived && (
                <Alert variant="warning">
                  <CircleAlert />
                  <Alert.Title>Posting failed</Alert.Title>
                  <Alert.Description>The Player can retry from their library.</Alert.Description>
                </Alert>
              )}
              <QaReturnedCallout videoProject={videoProject} onReview={openQaStudio} />
              {!formDisabled &&
              videoProject.editedMedia?.some(hasEditedVideo) &&
              !hasRequiredEditedSrt(videoProject.editedMedia) ? (
                <Alert variant="warning">
                  <CircleAlert />
                  <Alert.Title>Caption SRT file is missing</Alert.Title>
                  <Alert.Description>
                    You can still submit this project. Add an SRT if the cut has on-screen captions so QA can review
                    them.
                  </Alert.Description>
                </Alert>
              ) : null}
              {formErrorMessages && formErrorMessages.length > 0 && (
                <Alert
                  variant="destructive"
                  className="border-red-200 bg-red-50 text-red-900 *:data-[slot=alert-description]:text-red-900"
                >
                  <CircleAlert />
                  <Alert.Title>Fix these issues</Alert.Title>
                  <Alert.Description>
                    {formErrorMessages.map((err: string, i: number) => (
                      <p key={i}>{err}</p>
                    ))}
                  </Alert.Description>
                </Alert>
              )}
              <EditorPlayerInfo
                playerId={videoProject.playerId}
                industryRole={industryRole}
                videoProjectId={videoProject.id}
                coachId={videoProject.coachId}
              />

              <EditorProjectFiles videoProject={videoProject} />

              <EditorFinalEdit
                videoProject={videoProject}
                handleMediaUpdated={handleMediaUpdated}
                readOnly={formDisabled}
                onOpenQaStudio={openQaStudio}
              />

              <EditorPostInfo
                videoProject={videoProject}
                disabled={formDisabled}
                hasCaptionCoach={!!hasCaptionCoach}
                showHashtagToggle={!!showHashtagToggle}
                postableSocialNetworks={postableSocialNetworks}
                finalMessage={finalMessage}
                hashtags={hashtags}
                initialHashtags={initialHashtags}
                setHashtags={setHashtags}
                onChangeField={handleUpdateField}
                setShowAIModal={setShowAIModal}
                blockedWordsInCaption={blockedWordsInCaption}
              />
            </div>
            <div className="lg:col-span-2 flex flex-col gap-6">
              <ProjectInfoCard
                videoProject={videoProject}
                setVideoProject={setVideoProject}
                statusDisabled={
                  uploadingFiles ||
                  submittingProject ||
                  savingProject ||
                  startingQaReview ||
                  reopeningProject ||
                  savingStatus
                }
                onStatusColumnSelect={handleStatusColumnSelect}
                onQaFailedClick={openQaStudio}
              />
              <ClientNotesComponent
                playerId={videoProject?.playerId}
                coachId={videoProject?.coachId}
                playerNotes={playerNotes}
                coachNotes={coachNotes}
                onUpdate={(pNotes, cNotes) => {
                  setPlayerNotes(pNotes);
                  setCoachNotes(cNotes);
                }}
              />
              <StylePreferencesComponent preferencesInfo={preferencesInfo} formatHexColor={formatHexColor} />

              {videoProjectMessages && (
                <VideoProjectBubbleDetails
                  onSuccess={refreshMessages}
                  videoProjectId={videoProject.id}
                  submitBtnLabel={"Add Message"}
                  messages={videoProjectMessages}
                  needsAck={needsAck}
                />
              )}
              {canManageVideoProjectQueue && <VideoProjectHistoryPanel videoProjectId={videoProject.id} />}
            </div>
          </div>
          <VideoProjectQaStudio
            open={qaModalOpen}
            onOpenChange={setQaModalOpen}
            videoProject={videoProject}
            setVideoProject={setVideoProject}
            playerNotes={playerNotes}
            coachNotes={coachNotes}
            preferencesInfo={preferencesInfo}
            formatHexColor={formatHexColor}
            videoProjectMessages={videoProjectMessages}
            finalMessage={finalMessage}
            hashtags={hashtags}
            blockedWordsInCaption={blockedWordsInCaption}
            readOnly={isQaStudioReadOnly(videoProject.status, videoProject.archived)}
          />
        </>
      )}

      <UploadProgressModal open={uploadingFiles} progressMap={uploadProgressMap} />

      {showAIModal && (
        <CaptionCoachDialog
          open={showAIModal}
          onOpenChange={setShowAIModal}
          coachIdOverride={videoProject?.coachId}
          playerIdOverride={videoProject?.playerId}
          industryRoleOverride={industryRole || undefined}
          context={transcript}
          contextType="video transcript"
          handleAcceptedContent={({ caption: newCaption, hashtags: addedHashtags }) => {
            setShowAIModal(false);
            handleUpdateField("message", newCaption);

            if (addedHashtags && addedHashtags.length > 0) {
              const newTags: string[] = [];
              if (hashtags.length > 0) {
                hashtags.split(" ").forEach(tag => newTags.push(tag));
              }

              addedHashtags.forEach(tag => {
                if (newTags.indexOf(tag) === -1) {
                  newTags.push(tag);
                }
              });
              setHashtags(newTags.join(" "));
            }
          }}
        />
      )}
    </PageWrapper>
  );
}
